import { useCallback } from 'react'
import { X, History, Plus, Brain, BrainCircuit, Bot, ChevronDown, Download } from 'lucide-react'
import { ChatContextBadge } from './ChatContextBadge'
import type { ChatContext } from '../../hooks/useChatContext'
import type { ScopeOption } from './ChatScopeSelector'

interface Message {
  id?: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp?: string
}

interface Props {
  context: ChatContext
  selectedScope: ScopeOption
  messages: Message[]
  onClose: () => void
  onNewChat: () => void
  onToggleHistory: () => void
  onToggleScopeSelector: () => void
  useMemory: boolean
  onToggleMemory: () => void
  isConnected: boolean
}

export function ChatHeader({
  context: _context,
  selectedScope,
  messages,
  onClose,
  onNewChat,
  onToggleHistory,
  onToggleScopeSelector,
  useMemory,
  onToggleMemory,
  isConnected,
}: Props) {
  const handleDownloadSession = useCallback(() => {
    if (messages.length === 0) return

    const date = new Date().toISOString().split('T')[0]
    const scopeLabel = selectedScope.entityName
      ? `${selectedScope.scope}: ${selectedScope.entityName}`
      : selectedScope.scope

    let markdown = `# Chat Session Export\n\n`
    markdown += `**Date:** ${date}\n`
    markdown += `**Scope:** ${scopeLabel}\n\n`
    markdown += `---\n\n`

    for (const msg of messages) {
      const roleLabel = msg.role === 'user' ? '**You:**' : '**Assistant:**'
      markdown += `${roleLabel}\n\n${msg.content}\n\n---\n\n`
    }

    const blob = new Blob([markdown], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `chat-session-${date}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [messages, selectedScope])

  return (
    <div className="border-b border-gray-100">
      {/* Top row */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
            <Bot className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">AI Assistant</span>
              <span
                className={`inline-flex items-center w-2 h-2 rounded-full ${
                  isConnected ? 'bg-green-400' : 'bg-red-400'
                }`}
                title={isConnected ? 'Connected' : 'Disconnected'}
              />
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              <button
                onClick={onToggleScopeSelector}
                className="flex items-center gap-1 hover:bg-primary-100 rounded transition-colors cursor-pointer"
                title="Change chat scope"
              >
                <ChatContextBadge
                  context={{
                    scope: selectedScope.scope,
                    entityType: selectedScope.entityType,
                    entityId: selectedScope.entityId,
                    entityName: selectedScope.entityName,
                  }}
                />
                <ChevronDown className="w-2.5 h-2.5 text-primary-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Icon button group */}
        <div className="flex items-center gap-0.5 bg-gray-50 rounded-lg p-0.5">
          <button
            onClick={onToggleMemory}
            title={useMemory ? 'Memory enabled (click for fresh perspective)' : 'Memory disabled (click to enable)'}
            className={`p-1.5 rounded-md transition-colors ${
              useMemory ? 'text-primary-600 hover:bg-primary-50' : 'text-gray-400 hover:bg-gray-100'
            }`}
          >
            {useMemory ? <BrainCircuit className="w-4 h-4" /> : <Brain className="w-4 h-4" />}
          </button>
          <button
            onClick={onNewChat}
            className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 transition-colors"
            title="New chat"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            onClick={onToggleHistory}
            className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 transition-colors"
            title="Chat history"
          >
            <History className="w-4 h-4" />
          </button>
          <button
            onClick={handleDownloadSession}
            disabled={messages.length === 0}
            className={`p-1.5 rounded-md transition-colors ${
              messages.length === 0
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-400 hover:bg-gray-100'
            }`}
            title="Download chat session"
          >
            <Download className="w-4 h-4" />
          </button>
          <div className="w-px h-4 bg-gray-200 mx-0.5" />
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 transition-colors"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
